import { useState, useEffect } from 'react';
import { Navigate, Route } from 'react-router-dom';
import { googleAuthApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { UnifiedThemeProvider } from '@backstage/theme';
import CssBaseline from '@material-ui/core/CssBaseline';
import LightIcon from '@material-ui/icons/WbSunny';
import DarkIcon from '@material-ui/icons/Brightness2';
import { salsaDarkTheme, salsaLightTheme } from './theme';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import './assets/styles.css';

/** Redirects guests to /create, authenticated users to /catalog */
function HomeRedirect() {
  const identityApi = useApi(identityApiRef);
  const [target, setTarget] = useState<string | null>(null);

  useEffect(() => {
    identityApi.getBackstageIdentity().then(identity => {
      setTarget(
        identity.userEntityRef === 'user:default/nobody'
          ? '/create'
          : '/catalog',
      );
    });
  }, [identityApi]);

  if (!target) return null;
  return <Navigate to={target} replace />;
}

/** Blocks guest users from accessing a route, redirecting them to /create */
function GuestGuard({ children }: { children: JSX.Element }) {
  const identityApi = useApi(identityApiRef);
  const [allowed, setAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    identityApi.getBackstageIdentity().then(identity => {
      setAllowed(identity.userEntityRef !== 'user:default/nobody');
    });
  }, [identityApi]);

  if (allowed === null) return null;
  if (!allowed) return <Navigate to="/create" replace />;
  return children;
}

const app = createApp({
  apis,
  themes: [
    {
      id: 'salsa-light',
      title: 'Salsa Light',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={salsaLightTheme}>
          <CssBaseline />
          {children}
        </UnifiedThemeProvider>
      ),
    },
    {
      id: 'salsa-dark',
      title: 'Salsa Dark',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={salsaDarkTheme}>
          <CssBaseline />
          {children}
        </UnifiedThemeProvider>
      ),
    },
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        auto
        providers={[
          'guest',
          {
            id: 'google-auth-provider',
            title: 'Google',
            message: 'Sign in using Google',
            apiRef: googleAuthApiRef,
          },
          // {
          //   id: 'github-auth-provider',
          //   title: 'GitHub',
          //   message: 'Sign in using GitHub',
          //   apiRef: githubAuthApiRef,
          // }
        ]}
      />
    ),
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomeRedirect />} />
    <Route
      path="/catalog"
      element={
        <GuestGuard>
          <CatalogIndexPage initialKind="cloudapp" />
        </GuestGuard>
      }
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={
        <GuestGuard>
          <CatalogEntityPage />
        </GuestGuard>
      }
    >
      {entityPage}
    </Route>
    <Route
      path="/docs"
      element={
        <GuestGuard>
          <TechDocsIndexPage />
        </GuestGuard>
      }
    />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={
        <GuestGuard>
          <TechDocsReaderPage />
        </GuestGuard>
      }
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          contextMenu={{ editor: false, actions: false, tasks: false, create: false }}
        />
      }
    />
    <Route
      path="/api-docs"
      element={
        <GuestGuard>
          <ApiExplorerPage />
        </GuestGuard>
      }
    />
    <Route
      path="/catalog-import"
      element={
        <GuestGuard>
          <RequirePermission permission={catalogEntityCreatePermission}>
            <CatalogImportPage />
          </RequirePermission>
        </GuestGuard>
      }
    />
    <Route
      path="/search"
      element={
        <GuestGuard>
          <SearchPage />
        </GuestGuard>
      }
    >
      {searchPage}
    </Route>
    <Route
      path="/settings"
      element={
        <GuestGuard>
          <UserSettingsPage />
        </GuestGuard>
      }
    />
    <Route
      path="/catalog-graph"
      element={
        <GuestGuard>
          <CatalogGraphPage />
        </GuestGuard>
      }
    />
    <Route
      path="/notifications"
      element={
        <GuestGuard>
          <NotificationsPage />
        </GuestGuard>
      }
    />
    
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
